import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, CircularProgress, IconButton, Tooltip, Typography, Grid } from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import { DataTable } from '../../../contexts/dataTable';
import { getUnits } from '../../../actions/units.actions';
import Graphs from '../../../components/Graphs/Graphs';
import AddUnitsPage from './add/AddUnits';
import UnitsSearchForm from '../../../components/UnitsSearch/UnitsSearchForm';
import ArchiveUnit from './archive/ArchiveUnit';
import StatusUnitsDropdown from '../../../components/StatusDropdown/StatusUnitsDropdown';
import axios from 'axios';

const sortableColumns = [
  'unit_number',
];

const defaultPagination = {
  page: 1,
  page_size: 10
};

const defaultSearchParams = {
  customer: '',
  phone_number: '',
  unit_number: '',
  license_plate: '',
  status: '',
};

const UnitsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useState(defaultSearchParams);
  const [pagination, setPagination] = useState(defaultPagination);
  const [sort, setSort] = useState({});
  const [hasSearched, setHasSearched] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();
  const { data, meta, paging } = useSelector((state) => state.units);

  const cancelSearch = useRef(null);

  const handleRowClick = (e, row) => {
    const { id } = row.original;
    if (e.target.tagName === 'TD') {
      history.push(`/admin/units/${id}`);
    }
  };

  const handleClearSearch = useCallback(() => {
    setSearchParams(defaultSearchParams);
    setHasSearched(false);
    // Reset pagination when clearing search
    setPagination(defaultPagination);
  }, []);

  const handleSearch = useCallback(() => {
    setHasSearched(true);
    // Reset pagination when performing new search
    setPagination(defaultPagination);
    
    // Manually trigger API call when search button is clicked
    if (cancelSearch.current) {
      cancelSearch.current.cancel();
    }
    cancelSearch.current = axios.CancelToken.source();

    const apiParams = {
      page: 1, // Reset to first page for new search
      page_size: defaultPagination.page_size,
      ...sort,
    };

    // Add search parameters if they have values
    if (searchParams.customer && searchParams.customer.trim()) {
      apiParams.customer = searchParams.customer.trim();
    }
    if (searchParams.phone_number && searchParams.phone_number.trim()) {
      apiParams.phone_number = searchParams.phone_number.trim();
    }
    if (searchParams.unit_number && searchParams.unit_number.trim()) {
      apiParams.unit_number = searchParams.unit_number.trim();
    }
    if (searchParams.license_plate && searchParams.license_plate.trim()) {
      apiParams.license_plate = searchParams.license_plate.trim();
    }
    if (searchParams.status && searchParams.status.trim()) {
      apiParams.status = searchParams.status.trim();
    }

    setIsLoading(true);
    dispatch(getUnits(apiParams, cancelSearch.current.token)).catch((error) => {
      if (!axios.isCancel(error)) {
        throw new Error(error);
      }
    }).finally(() => setIsLoading(false));
  }, [searchParams, sort]);

  const getData = useCallback(() => {
    if (cancelSearch.current) {
      cancelSearch.current.cancel();
    }
    cancelSearch.current = axios.CancelToken.source();

    // Build API parameters based on search fields
    const apiParams = {
      ...pagination,
      ...sort,
    };

    // Only add search parameters if they have values and a search has been performed
    if (hasSearched) {
      if (searchParams.customer && searchParams.customer.trim()) {
        apiParams.customer = searchParams.customer.trim();
      }
      if (searchParams.phone_number && searchParams.phone_number.trim()) {
        apiParams.phone_number = searchParams.phone_number.trim();
      }
      if (searchParams.unit_number && searchParams.unit_number.trim()) {
        apiParams.unit_number = searchParams.unit_number.trim();
      }
      if (searchParams.license_plate && searchParams.license_plate.trim()) {
        apiParams.license_plate = searchParams.license_plate.trim();
      }
      if (searchParams.status && searchParams.status.trim()) {
        apiParams.status = searchParams.status.trim();
      }
    }

    setIsLoading(true);
    dispatch(getUnits(apiParams, cancelSearch.current.token)).catch((error) => {
      if (!axios.isCancel(error)) {
        throw new Error(error);
      }
    }).finally(() => setIsLoading(false))
  }, [hasSearched, sort, pagination]); // Removed searchParams from dependencies



  const handleSuccess = useCallback(() => {
    handleClearSearch();
    getData();
  }, [handleClearSearch, getData]);

  // Initial load - load all units without search filters
  useEffect(() => {
    if (!hasSearched) {
      getData();
    }
  }, [getData, hasSearched]);

  // Load data when pagination or sorting changes (only for searched results)
  useEffect(() => {
    if (hasSearched && (pagination.page > 1 || Object.keys(sort).length > 0)) {
      getData();
    }
  }, [getData, hasSearched, pagination, sort]);

  const columns = useMemo(
    () => [
      {
        Header: 'Unit #',
        accessor: 'unit_number',
      },
      {
        Header: 'Customer name',
        accessor: 'customerName',
        Cell({ row }) {
          const { first_name, last_name } = row.original;
          return `${first_name} ${last_name}`;
        },
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Phone number',
        accessor: 'phone_number',
      },
      {
        Header: 'LP',
        accessor: 'licence_plate',
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell({ row }) {
          return (
            <StatusUnitsDropdown
              id={row.original.id}
              statusChip={row.original.status}
            />
          );
        },
      },
      {
        Header: '',
        id: 'actions',
        Cell({ row }) {
          return (
            <ArchiveUnit
              unitStatus={row.original.status}
              unitId={row.original.id}
              renderOpen={(handleClickOpen) => (
                <Tooltip title="Archive Delete Unit">
                  <IconButton id='unit_archive_button' onClick={handleClickOpen}>
                    <Delete style={{ color: 'red' }} />
                  </IconButton>
                </Tooltip>
              )}
              handleSuccess={handleClearSearch}
            />
          );
        },
      },
    ],
    []
  );

  const isSearchActive = hasSearched && Object.values(searchParams).some(value => value && value.trim());

  return (
    <Box mr={4} pb={4}>
      <Graphs meta={meta} />
      <Grid container justify="space-between" alignItems="flex-start">
        <Grid item xs={12}>
          <UnitsSearchForm
            searchParams={searchParams}
            onSearchParamsChange={setSearchParams}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
      <Grid container justify="flex-end" style={{ marginBottom: 20 }}>
        <Grid item>
          <AddUnitsPage handleSuccess={handleSuccess} />
        </Grid>
      </Grid>
      {isLoading ? (
        <Box
          display='flex'
          justifyContent='center'
          mt={2}
          alignItems='center'
          height='50vh'
        >
          <CircularProgress color='primary' />
        </Box>
      ) : (
        <DataTable
          data={data}
          columns={columns}
          handlePaging={setPagination}
          paging={paging}
          initialState={{
            sortBy: [
              {
                id: 'unit_number',
                desc: false,
              }
            ],
            paginable: true
          }}
          sortingChanged={setSort}
          sort={sort}
          sortableColumns={sortableColumns}
          title="All units"
          handleRowClick={handleRowClick}
        />
      )}
      {!data.length && isSearchActive ? (
        <Typography variant="h5">No results</Typography>
      ) : null}
    </Box>
  );
};

export default UnitsPage;
